// Streaming microphone control.
//
// Live text streams in word-by-word over the app WebSocket while an
// AudioWorklet captures PCM continuously — the mic is never off between
// entries and capture never waits for the network (frames queue client-
// side; a parallel local MediaRecorder keeps the whole take as a safety
// net, offered for one-click re-send through the batch path if streaming
// ever failed outright).
//
// UI rule (John, 2026-07-20): while recording, never show a state that
// implies "stop talking". There are exactly two visible states — live
// text flowing, or a quiet "text catching up — keep going" hint. All
// degraded/reconnect handling is silent; reconciliation happens after
// stop.

import { useEffect, useRef, useState } from 'react';
import { useStore } from '../state/store';
import { startCapture, type CaptureHandle } from '../lib/audio';
import { StreamClient } from '../lib/streamClient';
import { SPEECH_LEVEL_PCT } from '@shared/level.js';
import type { StreamServerEvent } from '@shared/types.js';

const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

function pickMimeType(): string | null {
  const MR = (window as unknown as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== 'function') return null;
  for (const t of PREFERRED_MIME_TYPES) {
    if (MR.isTypeSupported(t)) return t;
  }
  return null;
}

function formatElapsed(sec: number): string {
  const mm = Math.floor(sec / 60).toString().padStart(2, '0');
  const ss = (sec % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

const CATCH_UP_AFTER_MS = 3000;

export function MicrophoneControl() {
  const { current, settings, toast } = useStore((s) => ({
    current: s.current,
    settings: s.settings,
    toast: s.toast,
  }));

  const [recording, setRecording] = useState(false);
  const [arming, setArming] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [levelPct, setLevelPct] = useState(0);
  const [peakPct, setPeakPct] = useState(0);
  const [liveText, setLiveText] = useState('');
  const [lastCommitted, setLastCommitted] = useState<string | null>(null);
  const [rowsCaptured, setRowsCaptured] = useState(0);
  const [catchingUp, setCatchingUp] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryReviewCount, setSummaryReviewCount] = useState(0);
  const [resendBlob, setResendBlob] = useState<Blob | null>(null);
  const [testVerdict, setTestVerdict] = useState<
    { kind: 'ok' | 'warn' | 'error'; text: string } | null
  >(null);

  const clientRef = useRef<StreamClient | null>(null);
  const captureRef = useRef<CaptureHandle | null>(null);
  const safetyRef = useRef<MediaRecorder | null>(null);
  const safetyChunksRef = useRef<BlobPart[]>([]);
  const safetyMimeRef = useRef<string>('audio/webm');
  const elapsedTimerRef = useRef<number | null>(null);
  const monitorTimerRef = useRef<number | null>(null);
  const stopTimeoutRef = useRef<number | null>(null);
  const testTimeoutRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const lastTextAtRef = useRef(0);
  const lastLoudAtRef = useRef(0);
  const spokeAtAllRef = useRef(false);
  const framesSentRef = useRef(0);
  const rowsRef = useRef(0);
  const reviewQueuedRef = useRef(0);
  const recordingRef = useRef(false);
  const lastGridRefreshRef = useRef(0);
  const testPeakRef = useRef(0);

  // One StreamClient for the component's lifetime, connected eagerly so
  // the pipe is warm before the first press.
  useEffect(() => {
    const client = new StreamClient({ onEvent: handleEvent });
    clientRef.current = client;
    client.connect();
    return () => {
      client.close();
      void teardownAudio();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Let other surfaces (Space in the empty transcript box) toggle
  // dictation. The ref always holds the closure over current state.
  const toggleRef = useRef<() => void>(() => {});
  toggleRef.current = () => {
    if (finishing || testing) return;
    if (recording || arming) stopRecording();
    else void startRecording();
  };
  useEffect(() => {
    useStore.setState({ requestMicToggle: () => toggleRef.current() });
    return () => {
      useStore.setState({ requestMicToggle: null });
    };
  }, []);

  function refreshGrid(force = false): void {
    const cur = useStore.getState().current;
    if (!cur) return;
    const now = Date.now();
    if (!force && now - lastGridRefreshRef.current < 400) return;
    lastGridRefreshRef.current = now;
    void useStore.getState().openSession(cur.id).catch(() => undefined);
  }

  function handleEvent(e: StreamServerEvent): void {
    if (e.type === 'delta') {
      lastTextAtRef.current = Date.now();
      setLiveText((prev) => (prev + e.text).slice(-500));
      setCatchingUp(false);
    } else if (e.type === 'entry-committed') {
      lastTextAtRef.current = Date.now();
      setLiveText('');
      setLastCommitted(e.entry);
      if (e.rowsAdded > 0) {
        rowsRef.current += e.rowsAdded;
        setRowsCaptured(rowsRef.current);
      } else {
        reviewQueuedRef.current += 1;
      }
      refreshGrid();
    } else if (e.type === 'stream-state') {
      // Deliberately no UI here while recording — see header comment.
    } else if (e.type === 'stopped') {
      finalizeStop(e.entries, e.reviewQueued);
    }
  }

  function startElapsedTimer(): void {
    startedAtRef.current = Date.now();
    setElapsedSec(0);
    elapsedTimerRef.current = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 200);
  }

  function stopTimers(): void {
    for (const ref of [elapsedTimerRef, monitorTimerRef]) {
      if (ref.current !== null) {
        window.clearInterval(ref.current);
        ref.current = null;
      }
    }
  }

  async function teardownAudio(): Promise<void> {
    if (safetyRef.current && safetyRef.current.state !== 'inactive') {
      try { safetyRef.current.stop(); } catch { /* fine */ }
    }
    safetyRef.current = null;
    if (captureRef.current) {
      await captureRef.current.stop().catch(() => undefined);
      captureRef.current = null;
    }
    setLevelPct(0);
  }

  async function startRecording(): Promise<void> {
    if (recording || arming || finishing || testing) return;
    const cur = useStore.getState().current;
    const s = useStore.getState().settings;
    if (!cur) { toast('warn', 'Open a session first'); return; }
    if (!s || s.transcriptionProvider === 'manual' || !s.cloudPrivacyAcknowledged) return;

    // Honest start cue: the mic takes a beat to open, and words spoken
    // before the first frame flows are physically not captured — that is
    // how "Twentieth of" became "Swansea F.C". Show "Starting mic…"
    // until frames actually arrive.
    setArming(true);
    let capture: CaptureHandle;
    try {
      capture = await startCapture({
        onFrame: (frame) => {
          if (framesSentRef.current === 0) setArming(false);
          framesSentRef.current += 1;
          clientRef.current?.pushFrame(frame);
        },
        onLevel: (pct) => {
          setLevelPct(pct);
          setPeakPct((prev) => (pct > prev ? pct : prev));
          if (pct >= SPEECH_LEVEL_PCT) {
            lastLoudAtRef.current = Date.now();
            spokeAtAllRef.current = true;
          }
        },
      });
    } catch (err) {
      setArming(false);
      toast('error', `Microphone unavailable: ${(err as Error).message}`);
      return;
    }
    captureRef.current = capture;

    // Local safety take, kept until the streamed rows reconcile.
    safetyChunksRef.current = [];
    const mime = pickMimeType();
    if (mime) {
      try {
        const rec = new MediaRecorder(capture.stream, { mimeType: mime });
        safetyMimeRef.current = mime;
        rec.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) safetyChunksRef.current.push(e.data);
        };
        rec.start(1000);
        safetyRef.current = rec;
      } catch {
        safetyRef.current = null; // streaming still works without the net
      }
    }

    rowsRef.current = 0;
    reviewQueuedRef.current = 0;
    framesSentRef.current = 0;
    spokeAtAllRef.current = false;
    setTestVerdict(null);
    setRowsCaptured(0);
    setLiveText('');
    setLastCommitted(null);
    setSummary(null);
    setResendBlob(null);
    setPeakPct(0);
    lastTextAtRef.current = Date.now();

    clientRef.current?.start(cur.id);
    setRecording(true);
    recordingRef.current = true;
    useStore.setState({ transcriptionStatus: 'listening' });
    startElapsedTimer();

    // Quiet catch-up hint: only when we are clearly speaking and sending
    // yet no text has arrived for a while. Never anything scarier.
    monitorTimerRef.current = window.setInterval(() => {
      if (!recordingRef.current) return;
      const now = Date.now();
      const speaking = now - lastLoudAtRef.current < 5000;
      const quiet = now - lastTextAtRef.current > CATCH_UP_AFTER_MS;
      setCatchingUp(speaking && quiet && framesSentRef.current > 20);
    }, 1000);
  }

  function stopRecording(): void {
    if (!recording && !arming) return;
    setArming(false);
    setRecording(false);
    recordingRef.current = false;
    setFinishing(true);
    setCatchingUp(false);
    stopTimers();
    void teardownAudio();
    clientRef.current?.stop();
    // If the summary never arrives (server unreachable), reconcile from
    // what we know locally rather than hanging in "finishing".
    stopTimeoutRef.current = window.setTimeout(() => {
      finalizeStop(rowsRef.current, reviewQueuedRef.current, true);
    }, 6000);
  }

  function finalizeStop(entries: number, reviewQueued: number, timedOut = false): void {
    if (stopTimeoutRef.current !== null) {
      window.clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    if (!finishing && !recordingRef.current && summary !== null) return; // already reconciled
    setFinishing(false);
    useStore.setState({ transcriptionStatus: 'disconnected' });
    const parts = [`${entries} row${entries === 1 ? '' : 's'} captured`];
    if (reviewQueued > 0) parts.push(`${reviewQueued} need${reviewQueued === 1 ? 's' : ''} attention`);
    setSummary(parts.join(' · '));
    setSummaryReviewCount(reviewQueued);
    setLiveText('');
    refreshGrid(true);

    // Offer the safety take only when speech clearly happened but nothing
    // came back — the one case where re-sending beats re-dictating.
    const nothingLanded = entries === 0 && reviewQueued === 0;
    if ((timedOut || nothingLanded) && spokeAtAllRef.current && safetyChunksRef.current.length > 0 && entries === 0) {
      setResendBlob(new Blob(safetyChunksRef.current, { type: safetyMimeRef.current }));
    } else {
      safetyChunksRef.current = [];
    }
  }

  async function resendSafetyTake(): Promise<void> {
    const blob = resendBlob;
    if (!blob) return;
    setResendBlob(null);
    const ext = safetyMimeRef.current.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([blob], `recovered-take.${ext}`, { type: safetyMimeRef.current });
    await useStore.getState().transcribeAudioFile('openai', file);
    safetyChunksRef.current = [];
  }

  // 5-second mic test: capture + level meter only, no network. Verdict
  // renders inline under the meter — corner toasts mid-test read as
  // errors and feel jarring.
  async function startMicTest(): Promise<void> {
    if (testing || recording || finishing) return;
    setTesting(true);
    setTestVerdict(null);
    testPeakRef.current = 0;
    let handle: CaptureHandle | null = null;
    try {
      handle = await startCapture({
        onFrame: () => {},
        onLevel: (pct) => {
          setLevelPct(pct);
          setPeakPct((prev) => (pct > prev ? pct : prev));
          if (pct > testPeakRef.current) testPeakRef.current = pct;
        },
      });
    } catch (err) {
      setTesting(false);
      setTestVerdict({ kind: 'error', text: `Microphone unavailable: ${(err as Error).message}` });
      return;
    }
    setPeakPct(0);
    testTimeoutRef.current = window.setTimeout(() => {
      void handle?.stop();
      setTesting(false);
      setLevelPct(0);
      const peak = testPeakRef.current;
      if (peak < 15) {
        setTestVerdict({
          kind: 'error',
          text: `Nothing heard (peak ${peak}%). The browser is not receiving audio — check System Settings → Privacy & Security → Microphone.`,
        });
      } else if (peak < 35) {
        setTestVerdict({
          kind: 'warn',
          text: `Heard you, but quietly (peak ${peak}%). It will work — speaking a little closer to the mic will help accuracy.`,
        });
      } else {
        setTestVerdict({ kind: 'ok', text: `Mic is working well (peak ${peak}%).` });
      }
    }, 5000);
  }

  // Spacebar toggles recording, exactly as before.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      const t = document.activeElement;
      const tag = t?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (t instanceof HTMLElement && t.isContentEditable) return;
      if (testing || finishing) return;
      const s = useStore.getState().settings;
      if (!s || s.transcriptionProvider === 'manual' || !s.cloudPrivacyAcknowledged) return;
      e.preventDefault();
      toggleRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, testing, finishing]);

  useEffect(() => () => {
    stopTimers();
    if (stopTimeoutRef.current !== null) window.clearTimeout(stopTimeoutRef.current);
    if (testTimeoutRef.current !== null) window.clearTimeout(testTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabled =
    settings?.transcriptionProvider !== 'manual' &&
    settings?.cloudPrivacyAcknowledged === true;

  if (!enabled) {
    return (
      <div className="mic mic--disabled" title="Configure a transcription provider in Settings">
        Mic disabled
      </div>
    );
  }

  const speaking = levelPct >= SPEECH_LEVEL_PCT;

  return (
    <div className={`mic ${recording ? 'mic--listening' : ''}`}>
      <button
        className={`mic-record ${recording ? 'mic-record--on' : ''}`}
        onClick={() => (recording ? stopRecording() : void startRecording())}
        disabled={testing || finishing || !current}
        title="Spacebar also toggles dictation"
      >
        {finishing
          ? 'Finishing up…'
          : arming
            ? '◌ Starting mic… (one moment)'
            : recording
              ? `● Recording ${formatElapsed(elapsedSec)} · ${rowsCaptured} row${rowsCaptured === 1 ? '' : 's'} — click or Space to stop`
              : '○ Click or Space to start dictating'}
      </button>
      <button
        type="button"
        onClick={() => void startMicTest()}
        disabled={recording || testing || finishing}
        title="5-second mic test that does not contact the server"
        style={{ marginLeft: 8 }}
      >
        {testing ? 'Testing… speak now' : 'Test mic (5s)'}
      </button>

      {(recording || testing) && (
        <div
          aria-label="Microphone input level"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 8,
            padding: '6px 8px',
            border: '1px solid #888',
            borderRadius: 4,
            background: '#0001',
            minWidth: 280,
          }}
        >
          <strong style={{ fontSize: 13 }}>
            {recording && speaking ? '● Hearing you' : 'Mic level'}
          </strong>
          <div
            style={{
              flex: 1,
              height: 14,
              background: '#0002',
              borderRadius: 7,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: `${levelPct}%`,
                height: '100%',
                background:
                  levelPct >= SPEECH_LEVEL_PCT ? '#2a8' : levelPct > 10 ? '#cc6' : '#c44',
                transition: 'width 80ms ease-out',
              }}
            />
          </div>
          <span style={{ fontFamily: 'monospace', fontSize: 13, minWidth: 70 }}>
            {levelPct}% (peak {peakPct}%)
          </span>
          {testing && (
            <span
              style={{
                color: levelPct >= SPEECH_LEVEL_PCT ? '#2a8' : '#886',
                fontSize: 12,
                minWidth: 90,
              }}
            >
              {levelPct >= SPEECH_LEVEL_PCT ? '✓ hearing you' : 'speak now…'}
            </span>
          )}
        </div>
      )}

      {testVerdict && !testing && !recording && (
        <div
          style={{
            marginTop: 8,
            padding: '6px 10px',
            borderRadius: 4,
            fontSize: 13,
            border: `1px solid ${
              testVerdict.kind === 'ok' ? '#2a8' : testVerdict.kind === 'warn' ? '#c84' : '#c44'
            }`,
          }}
        >
          {testVerdict.text}
        </div>
      )}

      {recording && (
        <div
          className="mic-live-pane"
          aria-live="polite"
          style={{
            marginTop: 8,
            padding: '8px 10px',
            border: '1px solid #888',
            borderRadius: 4,
            background: '#0001',
            fontSize: 13,
            minHeight: 40,
          }}
        >
          {lastCommitted && (
            <div style={{ opacity: 0.8 }}>✓ {lastCommitted}</div>
          )}
          {liveText ? (
            <div style={{ fontStyle: 'italic', opacity: 0.75 }}>{liveText}</div>
          ) : !lastCommitted ? (
            <div style={{ opacity: 0.5 }}>
              {arming
                ? 'Opening the microphone — start speaking when the level bar moves.'
                : 'Speak naturally — words appear here as you talk.'}
            </div>
          ) : null}
          {catchingUp && (
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.7 }}>
              text catching up — keep going, nothing is lost
            </div>
          )}
        </div>
      )}

      {!recording && !finishing && summary && (
        <div className="mic-summary" style={{ marginTop: 8, fontSize: 13 }}>
          {summary}
          {summaryReviewCount > 0 && (
            <button
              type="button"
              style={{ marginLeft: 8 }}
              onClick={() => useStore.getState().setView('review')}
            >
              Open review queue
            </button>
          )}
        </div>
      )}

      {resendBlob && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 10px',
            border: '1px solid #c84',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          That take couldn't be transcribed live, but the audio was kept.
          <button type="button" style={{ marginLeft: 8 }} onClick={() => void resendSafetyTake()}>
            Send recording now
          </button>
          <button
            type="button"
            style={{ marginLeft: 8 }}
            onClick={() => { setResendBlob(null); safetyChunksRef.current = []; }}
          >
            Discard
          </button>
        </div>
      )}
    </div>
  );
}
